import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Eye, EyeOff, X } from 'lucide-react';

const MAX_PASSWORD_LENGTH = 128;

export default function PublishPasswordModal({
  error,
  verifying,
  blocked = false,
  onCancel,
  onSubmit,
}) {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!blocked) {
      inputRef.current?.focus();
    }
  }, [blocked]);

  useEffect(() => {
    return () => {
      setPassword('');
      setShowPassword(false);
    };
  }, []);

  useEffect(() => {
    function onKeyDown(e) {
      if (e.key !== 'Escape' || verifying) return;
      e.preventDefault();
      onCancel();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onCancel, verifying]);

  const handleCancel = () => {
    setPassword('');
    setShowPassword(false);
    onCancel();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (verifying) return;
    const value = password.trim();
    if (!value) return;
    onSubmit(value);
  };

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[9990] bg-black/50"
        onClick={verifying ? undefined : handleCancel}
        role="presentation"
      />
      <div
        className="fixed top-1/2 left-1/2 z-[9991] w-full max-w-md -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="publish-verify-title"
      >
        <div className="border-b border-gray-200 bg-gradient-to-r from-slate-50 to-slate-100 px-6 py-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 id="publish-verify-title" className="text-lg font-bold text-main-dark">
                Verify admin access
              </h3>
              <p className="mt-1 text-xs text-main-light">
                Enter the shared admin password to unlock admin controls.
              </p>
            </div>
            <button
              type="button"
              onClick={handleCancel}
              disabled={verifying}
              className="rounded-lg p-2 text-main-light hover:bg-white/80 hover:text-main-dark disabled:opacity-50"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-6">
          {blocked ? (
            <>
              {error && (
                <p className="text-sm text-red-600" role="alert">
                  {error}
                </p>
              )}
              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="rounded-md bg-main-primary px-4 py-2 text-sm font-medium text-white hover:bg-main-primary/90"
                >
                  OK
                </button>
              </div>
            </>
          ) : (
            <>
          <label htmlFor="publish-verify-password" className="block text-xs font-medium text-main-light">
            Admin password
          </label>
          <div className="relative mt-1">
            <input
              ref={inputRef}
              id="publish-verify-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value.slice(0, MAX_PASSWORD_LENGTH))}
              disabled={verifying}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              maxLength={MAX_PASSWORD_LENGTH}
              className="w-full rounded-md border border-main-base py-2 pl-3 pr-10 text-sm text-main-dark focus:border-main-primary focus:ring-main-primary"
            />
            <button
              type="button"
              onClick={() => setShowPassword((visible) => !visible)}
              disabled={verifying}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-main-light hover:text-main-dark disabled:opacity-50"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Eye className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
          </div>
          {error && (
            <p className="mt-3 text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={handleCancel}
              disabled={verifying}
              className="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-main-dark hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={verifying || !password.trim()}
              className="inline-flex items-center gap-2 rounded-md bg-main-primary px-4 py-2 text-sm font-medium text-white hover:bg-main-primary/90 disabled:opacity-50"
            >
              {verifying && (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
              )}
              {verifying ? 'Verifying…' : 'Continue'}
            </button>
          </div>
            </>
          )}
        </form>
      </div>
    </>,
    document.body
  );
}
